import type {
  Addon,
  AddonCategory,
  AiJobs,
  EveryPlanInclude,
  FeatureGroups,
  PricingCatalog,
  PricingQuote,
} from '../../lib/sales-pricing';

/** Compact catalog slice so a /r share can render overview slides with no API. */
export interface ProposalEditionSnapshot {
  id: string;
  name: string;
  tagline: string;
  whoFor: string;
  included: string[];
  /** Present on 2026.09.2+ snapshots. Absent on older /r fragments. */
  unlimitedSeats?: boolean;
  /** Platform-fee list. Absent on 2026.09.1 seat-catalog snapshots. Null on custom-quote editions. */
  listAnnualMinor?: number | null;
  listIsFrom?: boolean;
  /** True when Finance must quote — no published platform fee (2026.09.3+). */
  customQuote?: boolean;
  includedFlowsAnnual?: number | null;
  includedAiSpendAnnualMinor?: number | null;
  featureGroups?: FeatureGroups;
  /** Legacy 2026.09.1 seat-catalog fields. */
  startingPerSeatAnnualMinor?: number;
  minSeats?: number;
  includedAnnual?: number;
  overagePerThousandMinor?: number;
}

export interface ProposalAddonSnapshot {
  id: string;
  name: string;
  category: AddonCategory;
  listAnnualMinor?: number;
  listOneTimeMinor?: number;
  prepaidRuns?: number;
  whoFor: string;
  editionIds?: string[];
}

export interface ProposalCatalogSnapshot {
  currency: string;
  editions: ProposalEditionSnapshot[];
  addons: ProposalAddonSnapshot[];
  everyPlanIncludes?: EveryPlanInclude[];
  aiJobs?: AiJobs;
}

/** Shared /r fragment payload for a branded pricing proposal deck. */
export interface PricingProposal {
  kind: 'pricing_proposal';
  catalogVersion: string;
  generatedAt: string;
  prospect: { companyName: string; contactName?: string };
  quote: PricingQuote;
  note?: string;
  /** Rep name shown on the cover (“Prepared by”). */
  preparedBy?: string;
  /** Calendar date `YYYY-MM-DD` — “Pricing valid until”. Legacy ISO timestamps are still accepted. */
  validUntil?: string;
  /** Optional snapshot so public /r links can render plan-overview slides. */
  catalog?: ProposalCatalogSnapshot;
}

export function isPricingProposal(x: unknown): x is PricingProposal {
  if (!x || typeof x !== 'object') return false;
  const p = x as PricingProposal;
  return p.kind === 'pricing_proposal' && !!p.quote && !!p.prospect?.companyName;
}

export function looksLikePricingProposal(x: unknown): boolean {
  if (!x || typeof x !== 'object') return false;
  return (x as { kind?: string }).kind === 'pricing_proposal';
}

/** Pre-platform-fee edition: per-seat list / includedAnnual, or no listAnnualMinor. */
export function isLegacySeatEdition(ed: ProposalEditionSnapshot): boolean {
  if (typeof ed.startingPerSeatAnnualMinor === 'number') return true;
  if (typeof ed.includedAnnual === 'number' && typeof ed.listAnnualMinor !== 'number') return true;
  return typeof ed.listAnnualMinor !== 'number' && typeof ed.minSeats === 'number';
}

export function catalogHasLegacySeatEditions(catalog: ProposalCatalogSnapshot | undefined): boolean {
  return (catalog?.editions ?? []).some(isLegacySeatEdition);
}

/** Included annual flows: new field, else the 2026.09.1 `includedAnnual`. */
export function editionIncludedFlows(ed: ProposalEditionSnapshot | undefined): number | null | undefined {
  if (!ed) return undefined;
  if (typeof ed.includedFlowsAnnual === 'number' || ed.includedFlowsAnnual === null) {
    return ed.includedFlowsAnnual;
  }
  if (typeof ed.includedAnnual === 'number') return ed.includedAnnual;
  return undefined;
}

/**
 * Make a stored /r catalog safe to render. Legacy seat editions keep their
 * original per-seat + include fields and never gain invented platform fees
 * or unlimited-seat flags.
 */
export function normalizeProposalForDeck(proposal: PricingProposal): PricingProposal {
  if (!proposal.catalog) return proposal;
  return {
    ...proposal,
    catalog: {
      ...proposal.catalog,
      editions: proposal.catalog.editions.map(normalizeEditionSnapshot),
    },
  };
}

function normalizeEditionSnapshot(ed: ProposalEditionSnapshot): ProposalEditionSnapshot {
  if (!isLegacySeatEdition(ed)) return ed;
  const flows =
    typeof ed.includedFlowsAnnual === 'number' || ed.includedFlowsAnnual === null
      ? ed.includedFlowsAnnual
      : typeof ed.includedAnnual === 'number'
        ? ed.includedAnnual
        : undefined;
  return {
    ...ed,
    unlimitedSeats: undefined,
    includedFlowsAnnual: flows,
  };
}

export function snapshotCatalog(catalog: PricingCatalog): ProposalCatalogSnapshot {
  return {
    currency: catalog.currency,
    editions: catalog.editions.map((ed) => ({
      id: ed.id,
      name: ed.name,
      tagline: ed.tagline,
      whoFor: ed.whoFor,
      included: [...ed.included],
      unlimitedSeats: true,
      listAnnualMinor: ed.listAnnualMinor,
      listIsFrom: ed.listIsFrom,
      customQuote: ed.customQuote,
      includedFlowsAnnual: ed.includedFlowsAnnual,
      includedAiSpendAnnualMinor: ed.includedAiSpendAnnualMinor,
      featureGroups: ed.featureGroups,
    })),
    addons: catalog.addons.map((a) => snapshotAddon(a)),
    everyPlanIncludes: catalog.everyPlanIncludes,
    aiJobs: catalog.aiJobs,
  };
}

function snapshotAddon(a: Addon): ProposalAddonSnapshot {
  return {
    id: a.id,
    name: a.name,
    category: a.category,
    listAnnualMinor: a.listAnnualMinor,
    listOneTimeMinor: a.listOneTimeMinor,
    prepaidRuns: a.prepaidRuns,
    whoFor: a.whoFor,
    editionIds: a.editionIds,
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** UTC calendar `YYYY-MM-DD` for a timestamp or date-only string. */
export function calendarDateUtc(iso: string): string | null {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (dateOnly) return `${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** Validity is a date-only calendar day (UTC of generatedAt + N days). */
export function defaultValidUntil(generatedAt: string, days = 30): string {
  const start = calendarDateUtc(generatedAt);
  if (!start) return generatedAt;
  const [y, m, d] = start.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return `${next.getUTCFullYear()}-${pad2(next.getUTCMonth() + 1)}-${pad2(next.getUTCDate())}`;
}

export function displayNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
